import { useState, useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { SPLASH_AUTO_DISMISS_MS } from '../release-notes-constants';
import type { ReleaseHighlight } from '../release-notes-types';
import { useI18n } from '../i18n';

interface SplashCardProps {
  highlight: ReleaseHighlight;
  onDismiss: () => void;
  onLearnMore: () => void;
}

export function SplashCard({ highlight, onDismiss, onLearnMore }: SplashCardProps) {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const remainingRef = useRef(SPLASH_AUTO_DISMISS_MS);
  const startRef = useRef(0);

  const startTimer = useCallback(() => {
    startRef.current = Date.now();
    timerRef.current = setTimeout(() => {
      setExiting(true);
    }, remainingRef.current);
  }, []);

  const pauseTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
      remainingRef.current -= Date.now() - startRef.current;
      if (remainingRef.current < 0) remainingRef.current = 0;
    }
  }, []);

  // Show with delay for animation
  useEffect(() => {
    const showTimer = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(showTimer);
  }, []);

  // Start auto-dismiss after visible
  useEffect(() => {
    if (visible && !exiting) startTimer();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [visible, exiting, startTimer]);

  // Handle exit animation end
  useEffect(() => {
    if (exiting) {
      const exitTimer = setTimeout(onDismiss, 300);
      return () => clearTimeout(exitTimer);
    }
  }, [exiting, onDismiss]);

  const handleClose = useCallback(() => {
    pauseTimer();
    setExiting(true);
  }, [pauseTimer]);

  const handleLearnMore = useCallback(() => {
    pauseTimer();
    onLearnMore();
  }, [pauseTimer, onLearnMore]);

  const className = [
    'splash-card',
    visible && !exiting ? 'splash-card--visible' : '',
    exiting ? 'splash-card--exit' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={className}
      onMouseEnter={pauseTimer}
      onMouseLeave={() => {
        if (visible && !exiting) startTimer();
      }}
      role="status"
      aria-live="polite"
    >
      <button
        className="splash-card__close"
        onClick={handleClose}
        aria-label={t('common.close')}
      >
        <X size={16} />
      </button>
      <div className="splash-card__emoji">{highlight.emoji}</div>
      <div className="splash-card__title">{highlight.title}</div>
      <div className="splash-card__desc">{highlight.description}</div>
      <Button variant="link" size="sm" onClick={handleLearnMore}>
        {t('releaseNotes.learnMore')}
      </Button>
    </div>
  );
}
